// Comments are page data, in the same doc as the page, so they sync and
// are kept the way edits are. A comment is anchored to a node, and if it
// is about some words, to stable cursors into that node's text: it follows
// its words through edits and its node through moves.
// meets: comments/anchor, comments/threads
import { Cursor, LoroList, LoroMap, type LoroDoc } from "loro-crdt";
import type { Page, TreeID } from "./doc";

export type Message = { by: string; at: number; text: string };
export type Thread = {
  id: string;
  node: TreeID;
  quote: string; // the words it was about, when it was made
  from?: number; // where those words are now
  to?: number;
  resolved: boolean;
  messages: Message[];
  gone: boolean; // its node was deleted
};

export class Comments {
  readonly map: LoroMap;
  constructor(private page: Page) {
    this.map = page.doc.getMap("comments");
  }

  private get doc(): LoroDoc {
    return this.page.doc;
  }

  add(node: TreeID, text: string, by: string, range?: { from: number; to: number }): string | undefined {
    if (!text.trim() || !this.page.node(node)) return undefined;
    const id = `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const t = this.map.setContainer(id, new LoroMap());
    t.set("node", node);
    const body = this.page.text(node);
    if (range && body && range.to > range.from) {
      t.set("anchor", Array.from(body.getCursor(range.from)!.encode()) as never);
      // The end sticks to the last character, so typing just after the
      // words doesn't join them.
      t.set("head", Array.from(body.getCursor(range.to - 1)!.encode()) as never);
      t.set("quote", body.toString().slice(range.from, range.to));
    } else t.set("quote", "");
    t.set("resolved", false);
    const list = t.setContainer("messages", new LoroList());
    list.push({ by, at: Date.now(), text } as never);
    this.doc.commit({ origin: "aside:comment" });
    return id;
  }

  reply(id: string, text: string, by: string) {
    const t = this.map.get(id);
    if (!(t instanceof LoroMap) || !text.trim()) return;
    (t.get("messages") as LoroList).push({ by, at: Date.now(), text } as never);
    this.doc.commit({ origin: "aside:comment" });
  }

  resolve(id: string, resolved: boolean) {
    const t = this.map.get(id);
    if (!(t instanceof LoroMap)) return;
    t.set("resolved", resolved);
    this.doc.commit({ origin: "aside:comment" });
  }

  // Every thread, oldest first, with where its words are now.
  list(): Thread[] {
    const out: Thread[] = [];
    const pos = (c: unknown) => {
      if (!Array.isArray(c) || !c.length) return undefined;
      try {
        return this.doc.getCursorPos(Cursor.decode(new Uint8Array(c)))?.offset;
      } catch {
        return undefined;
      }
    };
    for (const id of this.map.keys()) {
      const t = this.map.get(id);
      if (!(t instanceof LoroMap)) continue;
      const node = String(t.get("node")) as TreeID;
      const messages = (t.get("messages") as LoroList | undefined)?.toJSON() as Message[] | undefined;
      if (!messages?.length) continue;
      const from = pos(t.get("anchor"));
      const last = pos(t.get("head"));
      const to = last === undefined ? undefined : last + 1;
      out.push({
        id,
        node,
        quote: String(t.get("quote") ?? ""),
        ...(from !== undefined && to !== undefined && to > from ? { from, to } : {}),
        resolved: t.get("resolved") === true,
        messages,
        gone: !this.page.node(node),
      });
    }
    return out.sort((a, b) => a.messages[0].at - b.messages[0].at);
  }
}
